import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import RouteMap from "@/components/RouteMap";
import { Loader2, MapPin, Clock, Route, Sparkles, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";

function shiftDate(iso, days) {
  const d = iso ? new Date(iso + "T00:00:00") : new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

/**
 * Daily route + named stops + AI "likely activity" for one vehicle (and whoever
 * rode it that day). Used on the Vehicle History page and the Delivery rider view.
 */
export default function DayRoutePanel({ vehicleId, title = "Where it went" }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (refresh) => {
    refresh ? setRefreshing(true) : setLoading(true);
    try {
      const { data: d } = await api.get(`/operator/analytics/vehicles/${vehicleId}/day-intel`, { params: { date, refresh } });
      setData(d);
    } catch { setData(null); }
    finally { setLoading(false); setRefreshing(false); }
  }, [vehicleId, date]);

  useEffect(() => { load(false); }, [load]);

  const s = data?.summary || {};
  const stops = data?.stops || [];
  const areas = data?.areas || [];

  return (
    <div data-testid="day-route-panel" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-base font-semibold"><Route className="h-4 w-4 text-tenant-primary" /> {title}</h3>
        <div className="flex items-center gap-1">
          <button data-testid="day-prev" onClick={() => setDate((d) => shiftDate(d, -1))} className="rounded-tenant-button border border-tenant-border p-1.5 hover:bg-tenant-bg"><ChevronLeft className="h-4 w-4" /></button>
          <input data-testid="day-date" type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} className="rounded-tenant-button border border-tenant-border bg-tenant-surface px-2.5 py-1 text-sm" />
          <button data-testid="day-next" disabled={date >= today} onClick={() => setDate((d) => shiftDate(d, 1))} className="rounded-tenant-button border border-tenant-border p-1.5 hover:bg-tenant-bg disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
          <button data-testid="day-refresh" onClick={() => load(true)} disabled={refreshing} title="Regenerate AI summary" className="ml-1 rounded-tenant-button border border-tenant-border p-1.5 hover:bg-tenant-bg disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : (
        <>
          <RouteMap route={data?.route || []} stops={stops} height={300} />

          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            <div className="rounded-tenant-button border border-tenant-border bg-tenant-bg p-3"><p className="text-xs text-muted-foreground">Distance</p><p className="mt-0.5 font-semibold">{s.distance_km ?? 0} km</p></div>
            <div className="rounded-tenant-button border border-tenant-border bg-tenant-bg p-3"><p className="text-xs text-muted-foreground">Stops</p><p className="mt-0.5 font-semibold">{stops.length}</p></div>
            <div className="rounded-tenant-button border border-tenant-border bg-tenant-bg p-3"><p className="text-xs text-muted-foreground">Moving time</p><p className="mt-0.5 font-semibold">{s.moving_minutes ?? 0} min</p></div>
            <div className="rounded-tenant-button border border-tenant-border bg-tenant-bg p-3"><p className="text-xs text-muted-foreground">Ridden by</p><p className="mt-0.5 truncate font-semibold">{(data?.riders || []).join(", ") || "—"}</p></div>
          </div>

          {data?.narrative && (
            <div data-testid="day-narrative" className="rounded-tenant-button border border-tenant-primary/25 bg-tenant-primary/5 p-4">
              <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-tenant-primary"><Sparkles className="h-3.5 w-3.5" /> AI activity summary</p>
              <p className="text-sm leading-relaxed text-foreground">{data.narrative}</p>
            </div>
          )}

          {areas.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Areas:</span>
              {areas.map((a) => <span key={a} className="rounded-full bg-tenant-bg px-2.5 py-0.5 text-xs text-foreground">{a}</span>)}
            </div>
          )}

          {stops.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-muted-foreground">Stops timeline</p>
              {stops.map((st, i) => (
                <div key={i} data-testid={`stop-${i}`} className="flex items-center gap-3 rounded-tenant-button border border-tenant-border px-3 py-2 text-sm">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-tenant-primary/10 text-xs font-bold text-tenant-primary">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium"><MapPin className="mr-1 inline h-3.5 w-3.5 text-tenant-primary" />{st.place || "Stop"}</p>
                    {st.address && <p className="truncate text-xs text-muted-foreground">{st.address}</p>}
                  </div>
                  <span className="shrink-0 text-xs text-muted-foreground"><Clock className="mr-1 inline h-3 w-3" />{fmtTime(st.arrive)} · {st.dwell_min}m</span>
                </div>
              ))}
            </div>
          )}

          {!data?.route?.length && stops.length === 0 && (
            <p className="rounded-tenant-button border border-dashed border-tenant-border px-4 py-6 text-center text-sm text-muted-foreground">No movement recorded on this day.</p>
          )}
        </>
      )}
    </div>
  );
}
